// §10.6 Deal Terminal Timeline — top-level layout for the opportunity
// detail page. Replaces the prior LT-152 / LT-336 detail page.
//
// Why this shape (keep this comment around for the next maintainer):
//
//   Same opportunity may close lost → reopen → close won. Reporting
//   must respect every cycle. Mark won / Mark lost / Reopen each
//   APPEND an immutable event to `opportunity_events`. The legacy
//   overwrite-on-close model (`win_loss_reason` overwritten each
//   time) is gone — the cycle counters, cycle bar, and loss-reason
//   rollup are all derived live from the events array.
//
//   The header actions are state-aware: when the deal is open, the
//   user sees Mark won + Mark lost. When it's closed, they see Reopen
//   only. Reopen never appears alongside Mark won/lost — the spec is
//   explicit, and the data model makes it impossible anyway.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import PageLoader from '../../../components/PageLoader';
import PageShell from '../../../components/PageShell';
import { Card, Pill } from '../../../ui';
import { opportunitiesAPI } from '../../../services/opportunitiesAPI';
import { pipelinesAPI } from '../../../services/pipelinesAPI';
import { accountsAPI } from '../../../services/accountsAPI';
import { contactsAPI } from '../../../services/contactsAPI';
import type { Account, Contact } from '../../../types/contact';
import type {
  OpportunityEvent,
  OpportunityWithEvents,
  Pipeline,
  PipelineStage,
} from '../../../types/pipeline';

import DealActionButtons from './DealActionButtons';
import DealConfirmModal, { DealActionKind } from './DealConfirmModal';
import DealCycleBar from './DealCycleBar';
import DealCycleCounters from './DealCycleCounters';
import DealEventTimeline from './DealEventTimeline';
import DealLossRollup from './DealLossRollup';
import { CycleFilter, EventFilter } from './state';

function extractMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { detail?: unknown } } };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (
    detail &&
    typeof detail === 'object' &&
    'message' in (detail as Record<string, unknown>)
  ) {
    return String((detail as Record<string, unknown>).message);
  }
  return fallback;
}

function fmtCurrency(amount?: number | null, currency?: string | null): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency ?? ''} ${amount}`;
  }
}

const DealTerminalPage: React.FC = () => {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const navigate = useNavigate();

  const [opp, setOpp] = useState<OpportunityWithEvents | null>(null);
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const [filter, setFilter] = useState<EventFilter>('all');
  const [cycleFilter, setCycleFilter] = useState<CycleFilter>('all');
  const [collapsedCycles, setCollapsedCycles] = useState<Record<number, boolean>>(
    {},
  );

  const [pending, setPending] = useState<DealActionKind | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!opportunityId) return;
    setLoading(true);
    setErr(null);
    try {
      const fetched = await opportunitiesAPI.get(opportunityId);
      setOpp(fetched);

      const [p, acc, c] = await Promise.all([
        pipelinesAPI.get(fetched.pipeline_id),
        accountsAPI.get(fetched.account_id),
        contactsAPI.get(fetched.primary_contact_id),
      ]);
      setPipeline(p);
      setAccount(acc);
      setContact(c);
    } catch (e) {
      setErr(extractMessage(e, 'Failed to load opportunity'));
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleCycle = useCallback((cycle: number) => {
    setCollapsedCycles((s) => ({ ...s, [cycle]: !s[cycle] }));
  }, []);

  const handleStageChange = async (newStageId: string) => {
    if (!opp || newStageId === opp.stage_id) return;
    try {
      await opportunitiesAPI.advance(opp.id, { to_stage_id: newStageId });
      await load();
      setToast({ type: 'success', text: 'Stage updated' });
    } catch (e) {
      setToast({ type: 'error', text: extractMessage(e, 'Could not advance stage') });
    }
  };

  const handleConfirm = async ({
    kind,
    reason,
    lossKind,
  }: {
    kind: DealActionKind;
    reason: string;
    lossKind: string | null;
  }) => {
    if (!opp) return;
    setSaving(true);
    try {
      await opportunitiesAPI.appendEvent(opp.id, {
        kind,
        reason: reason || null,
        loss_kind: kind === 'lost' ? (lossKind as never) : null,
      });
      setPending(null);
      await load();
    } catch (e) {
      setToast({ type: 'error', text: extractMessage(e, 'Could not append event') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;
  if (err || !opp) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto p-6">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-300 hover:text-white flex items-center gap-1 text-sm mb-3"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="bg-danger-tint border border-danger-border text-danger-text rounded px-3 py-2">
            {err ?? 'Opportunity not found.'}
          </div>
        </div>
      </PageShell>
    );
  }

  const events: OpportunityEvent[] = opp.events ?? [];
  const isOpen = opp.is_open;
  const currentCycle = opp.current_cycle;
  const cycleAfterReopen = currentCycle + 1;

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        <button
          onClick={() => navigate('/deals')}
          className="text-slate-300 hover:text-white flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to deals
        </button>

        {toast && (
          <div
            className={`px-3 py-2 rounded text-sm flex items-center justify-between ${
              toast.type === 'success'
                ? 'bg-ok-tint border border-ok-border text-ok-text'
                : 'bg-danger-tint border border-danger-border text-danger-text'
            }`}
          >
            <span>{toast.text}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ml-2 text-slate-400 hover:text-white"
            >
              ×
            </button>
          </div>
        )}

        <PageHeader
          opp={opp}
          pipeline={pipeline}
          isOpen={isOpen}
          currentCycle={currentCycle}
          saving={saving}
          onStageChange={handleStageChange}
          onMarkWon={() => setPending('won')}
          onMarkLost={() => setPending('lost')}
          onReopen={() => setPending('reopen')}
        />

        <div className="grid gap-3" style={{ gridTemplateColumns: '300px 1fr 300px' }}>
          {/* LEFT — facts + counters */}
          <div className="flex flex-col gap-3">
            <Card padding="tight">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-mono mb-1.5">
                Identity
              </div>
              <dl className="text-[13px] leading-7">
                <Row
                  label="Account"
                  value={
                    account ? (
                      <Link
                        to={`/accounts/${account.id}`}
                        className="text-accent-text hover:underline"
                      >
                        {account.name}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <Row
                  label="Primary contact"
                  value={
                    contact ? (
                      <Link
                        to={`/contacts/${contact.id}`}
                        className="text-accent-text hover:underline"
                      >
                        {`${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() ||
                          contact.email ||
                          contact.phone ||
                          contact.id}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <Row
                  label="Amount"
                  value={
                    <span className="font-mono">
                      {fmtCurrency(opp.amount, opp.currency)}
                    </span>
                  }
                />
                <Row label="Currency" value={opp.currency ?? '—'} />
                <Row
                  label="Stage"
                  value={
                    pipeline?.stages.find((s) => s.id === opp.stage_id)?.name ??
                    '—'
                  }
                />
              </dl>
            </Card>

            <Card padding="tight">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-mono">
                Sourced from
              </div>
              <div className="text-[13px] mt-1.5">
                <div className="font-medium text-slate-200">
                  {opp.source ?? '—'}
                </div>
                {opp.source && (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    deal source attribution
                  </div>
                )}
              </div>
            </Card>

            <DealCycleCounters events={events} currentCycle={currentCycle} />
          </div>

          {/* CENTER — filters + cycle summary bar + timeline */}
          <Card padding="default">
            <DealCycleBar
              events={events}
              collapsedCycles={collapsedCycles}
              onToggle={toggleCycle}
            />
            <DealEventTimeline
              events={events}
              filter={filter}
              setFilter={setFilter}
              cycleFilter={cycleFilter}
              setCycleFilter={setCycleFilter}
              collapsedCycles={collapsedCycles}
              currentCycle={currentCycle}
            />
          </Card>

          {/* RIGHT — loss-reason rollup + append-only callout */}
          <div className="flex flex-col gap-3">
            <DealLossRollup events={events} />
            <Card padding="default">
              <div className="text-[11px] uppercase tracking-wide text-ok-text font-mono mb-1.5">
                Why append-only
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Same opportunity may close lost, reopen, then close won.
                Reporting must respect both. Overwriting a single
                close-reason field on every close is dishonest — this
                timeline is the only honest representation.
              </p>
            </Card>
          </div>
        </div>
      </div>

      <DealConfirmModal
        pending={pending}
        cycleAfterReopen={cycleAfterReopen}
        saving={saving}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirm}
      />
    </PageShell>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex gap-2">
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-slate-200">·</dd>
    <dd className="text-slate-200">{value}</dd>
  </div>
);

interface PageHeaderProps {
  opp: OpportunityWithEvents;
  pipeline: Pipeline | null;
  isOpen: boolean;
  currentCycle: number;
  saving: boolean;
  onStageChange: (id: string) => void;
  onMarkWon: () => void;
  onMarkLost: () => void;
  onReopen: () => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  opp,
  pipeline,
  isOpen,
  currentCycle,
  saving,
  onStageChange,
  onMarkWon,
  onMarkLost,
  onReopen,
}) => {
  const stages: PipelineStage[] = pipeline?.stages ?? [];
  return (
    <div className="flex flex-wrap items-start gap-3 justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">
          Deal · multi-cycle
        </div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-50 mt-1">
          {opp.name}
        </h1>
        <p className="text-xs text-slate-400 mt-1 max-w-xl">
          Append-only Deal Terminal History. Mark won / Mark lost / Reopen the
          deal — every action captures a reason and appends an immutable
          event.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Pill intent={isOpen ? 'ok' : 'neutral'}>
          {isOpen
            ? `open · cycle ${currentCycle}`
            : `closed · cycle ${currentCycle}`}
        </Pill>
        {isOpen && stages.length > 0 && (
          <select
            aria-label="Stage"
            value={opp.stage_id}
            onChange={(e) => onStageChange(e.target.value)}
            disabled={saving}
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-[12px]"
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                Stage: {s.name}
              </option>
            ))}
          </select>
        )}
        <DealActionButtons
          isOpen={isOpen}
          disabled={saving}
          onMarkWon={onMarkWon}
          onMarkLost={onMarkLost}
          onReopen={onReopen}
        />
      </div>
    </div>
  );
};

export default DealTerminalPage;
